import type { PropsWithChildren, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/branding/brand-logo';
import { AccountMenu } from '@/components/common/account-menu';
import { OnboardingOrganizationSwitcher } from '@/features/onboarding/components/onboarding-organization-switcher';
import { OnboardingStepper } from '@/features/onboarding/components/onboarding-stepper';
import { type OnboardingStep } from '@/features/onboarding/data';
import { cn } from '@/lib/utils';

type OnboardingShellProps = PropsWithChildren<{
    currentStep: OnboardingStep;
    title: string;
    description: string;
    rightPane?: ReactNode;
    footer?: ReactNode;
}>;

export function OnboardingShell({
    currentStep,
    title,
    description,
    rightPane,
    footer,
    children
}: OnboardingShellProps) {
    const { t } = useTranslation();

    return (
        <div className='min-h-screen bg-muted/40 text-foreground'>
            <div
                aria-hidden='true'
                className='pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(60%_60%_at_50%_0%,oklch(0.785_0.115_274.713_/_0.14),transparent_70%)]'
            />

            <header className='sticky top-0 z-40 border-b bg-background/80 backdrop-blur'>
                <div className='mx-auto flex min-h-20 max-w-2xl flex-wrap items-center gap-3 px-6 py-4 sm:flex-nowrap'>
                    <Link to='/onboarding' className='flex shrink-0 items-center'>
                        <BrandLogo className='[&_img]:h-5' />
                    </Link>
                    <div className='order-3 w-full font-mono text-xs text-muted-foreground sm:order-2 sm:ml-auto sm:w-auto'>
                        {t('onboardingFlow.shell.counter', {
                            current: String(currentStep).padStart(2, '0'),
                            total: '02'
                        })}
                    </div>
                    <div className='order-4 flex w-full items-center justify-end gap-2 sm:order-3 sm:w-auto'>
                        <OnboardingOrganizationSwitcher />
                        <AccountMenu />
                    </div>
                </div>
            </header>

            <main className='relative mx-auto max-w-2xl px-6 py-10'>
                <div className='mb-12'>
                    <OnboardingStepper currentStep={currentStep} />
                </div>
                <section className='rounded-2xl border bg-card p-8 shadow-none'>
                    {title || description ? (
                        <div className='mb-8'>
                            {title ? (
                                <h1 className='text-3xl font-semibold tracking-tight text-foreground'>{title}</h1>
                            ) : null}
                            {description ? (
                                <p className='mt-2 text-[15px] text-muted-foreground'>{description}</p>
                            ) : null}
                        </div>
                    ) : null}
                    <div className={cn(rightPane ? 'space-y-8' : '')}>
                        {children}
                        {rightPane ? <div>{rightPane}</div> : null}
                    </div>
                    {footer ? <div className='mt-8'>{footer}</div> : null}
                </section>
            </main>
        </div>
    );
}
